import React, { CSSProperties, ReactNode, useState } from 'react';
import Select, { ActionMeta, components, MultiValue, SingleValue } from 'react-select';

import styled from '@emotion/styled';
import { colors, radii } from '@opendoor/bricks/theme/eero';

export const ALL = { value: 'All', label: 'All' };
export type OptionType = { value: string; label: string };

export interface IDropdownMultiSelectProps {
  options: Array<OptionType>;
  includeAllOption: boolean;
  defaultValues: Array<OptionType>;
  onChange: (values: Array<OptionType> | undefined) => void;
  selectionSummary: (values: Array<OptionType> | undefined) => ReactNode;
}

export const DropdownMultiSelect = ({ ...props }: IDropdownMultiSelectProps) => {
  const options = props.includeAllOption ? [ALL, ...props.options] : props.options;
  const defaultValues =
    props.includeAllOption && props.defaultValues === props.options ? options : props.defaultValues;

  const [values, setValues] = useState(defaultValues);
  const [isOpen, setOpen] = useState(false);

  const toggleOpen = () => {
    setOpen(!isOpen);
  };

  const onChange = (value: MultiValue<OptionType>, _action: ActionMeta<OptionType>) => {
    const values = value as Array<OptionType>;
    const action = _action as ActionMeta<OptionType> & { option: OptionType };

    let newValues: Array<OptionType>;
    if (
      action.action === 'select-option' &&
      (action.option.value === ALL.value || values.length === props.options.length)
    ) {
      newValues = [ALL, ...options];
    } else if (action.action === 'deselect-option' && action.option.value === ALL.value) {
      newValues = [];
    } else if (action.action === 'deselect-option' && action.option.value !== ALL.value) {
      newValues = values.filter((o) => o.value !== ALL.value);
    } else {
      newValues = values;
    }

    setValues(newValues);
    props.onChange(newValues);
  };

  return (
    <Dropdown
      isOpen={isOpen}
      onClose={toggleOpen}
      target={
        <button
          onClick={toggleOpen}
          style={{
            ...toggleStyles(isOpen),
            overflow: 'hidden',
            width: '135px',
            color: '#555B77',
          }}
        >
          {props.selectionSummary(values)}
          <components.DownChevron style={dropdownIndicatorStyles} />
        </button>
      }
    >
      <Select
        options={options}
        onChange={onChange}
        value={values}
        styles={selectStyles}
        isMulti
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        menuIsOpen
        placeholder={'Search'}
        closeMenuOnSelect={false}
        isClearable={false}
        tabSelectsValue={false}
        backspaceRemovesValue={false}
        hideSelectedOptions={false}
        controlShouldRenderValue={false}
        components={{
          Option: OptionMulti,
          IndicatorSeparator: null,
          DropdownIndicator: null,
          MenuList,
          Input,
        }}
      />
    </Dropdown>
  );
};

const OptionMulti = (props: any) => {
  const innerProps = {
    ...props.innerProps,
    role: 'option',
    'aria-disabled': props.isDisabled,
  };

  return (
    <OptionContainer isSelected={props.isSelected}>
      <components.Option {...props} innerProps={innerProps}>
        <SquareSelectorIcon isSelected={props.isSelected}>
          <div id={'bottomSq'} />
        </SquareSelectorIcon>
        <span style={{ lineHeight: '16px' }}>{props.children}</span>
      </components.Option>
    </OptionContainer>
  );
};

const selectStyles = {
  control: (base: any) => ({ ...base, border: 'none', minWidth: 240, margin: 8 }),
  placeholder: (base: any) => ({ ...base, color: '#525975' }),
  menu: () => ({ borderTop: '0.5px solid #D2E0EA' }),
  option: () => ({
    ...optionStyles,
    color: colors.brand50,
    padding: '8px 12px',
  }),
};

const SquareSelectorIcon = styled.div<{ isSelected: boolean }>`
  background-color: ${colors.neutrals0};
  border-radius: 2px;
  border: 1px solid ${colors.brand50};
  display: inline-block;
  margin: 0 14px 0 0;

  div {
    background-color: ${(props: any) => (props.isSelected ? colors.brand50 : colors.neutrals0)};
    border-radius: 1px;
    height: 14px;
    margin: 3px;
    width: 14px;
  }
`;

interface IDropdownProps {
  children: JSX.Element;
  isOpen: boolean;
  target: JSX.Element;
  onClose: () => void;
}

export const Dropdown = ({ children, isOpen, target, onClose }: IDropdownProps) => (
  <div style={{ position: 'relative' }}>
    {target}
    {isOpen ? <Menu>{children}</Menu> : null}
    {isOpen ? <Blanket onClick={onClose} /> : null}
  </div>
);

const Menu = (props: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      style={{
        backgroundColor: colors.neutrals0,
        borderRadius: 4,
        boxShadow: '0px 10px 25px rgba(0, 0, 0, 0.1)',
        position: 'absolute',
        zIndex: 2,
      }}
      {...props}
    />
  );
};

const Blanket = (props: any) => (
  <div
    style={{
      bottom: 0,
      left: 0,
      position: 'fixed',
      right: 0,
      top: 0,
      zIndex: 1,
    }}
    {...props}
  />
);

export interface IDropdownSelectProps {
  options: Array<OptionType>;
  defaultValue?: OptionType;
  onChange: (values: OptionType | undefined) => void;
  value?: OptionType;
}

export const DropdownSelect: React.FC<IDropdownSelectProps> = ({
  defaultValue,
  options,
  value,
  onChange,
}) => {
  const handleChange = (valuee: SingleValue<OptionType>, _action: ActionMeta<OptionType>) => {
    const value = valuee as OptionType;
    onChange(value);
  };

  return (
    <Select
      defaultValue={defaultValue}
      options={options}
      onChange={handleChange}
      value={value}
      styles={selectStylesSingle}
      isSearchable={false}
      isClearable={false}
      tabSelectsValue={false}
      backspaceRemovesValue={false}
      components={{
        Option: OptionSingle,
        IndicatorSeparator: null,
        MenuList,
        Input,
      }}
    />
  );
};

const selectStylesSingle = {
  control: (_base: any, { menuIsOpen }: { menuIsOpen: boolean }) => ({
    minWidth: 160,
    margin: 0,
    border: 'none',
    ...toggleStyles(menuIsOpen),
  }),
  dropdownIndicator: () => dropdownIndicatorStyles,
  singleValue: (base: any) => ({ ...base, color: colors.neutrals100 }),
  placeholder: (base: any) => ({ ...base, color: colors.neutrals100 }),
  menu: (base: any) => ({
    ...base,
    minWidth: 200,
    boxShadow: '0px 10px 25px rgba(0, 0, 0, 0.1);',
    marginTop: 0,
  }),
  option: () => ({
    ...optionStyles,
    padding: '20px 12px',
  }),
};

/**
 * Component has been customized to add complete a11y support for the select component.
 * @see https://github.com/JedWatson/react-select/issues/4062#issuecomment-872356845 for more details on what the current stance of react-select is.
 * @see https://github.com/JedWatson/react-select/pull/4322#issuecomment-749134033 for workaround
 */
const OptionSingle = (props: any) => {
  const innerProps = {
    ...props.innerProps,
    role: 'option',
    'aria-disabled': props.isDisabled,
  };

  return (
    <OptionContainer isSelected={props.isSelected}>
      <components.Option {...props} innerProps={innerProps}>
        <span style={{ lineHeight: '16px' }}>{props.children}</span>
      </components.Option>
    </OptionContainer>
  );
};

/**
 * Component has been customized to add complete a11y support for the select component.
 * @see https://github.com/JedWatson/react-select/issues/4062#issuecomment-872356845 for more details on what the current stance of react-select is.
 * @see https://github.com/JedWatson/react-select/pull/4322#issuecomment-749134033 for workaround
 */
const MenuList = (props: any) => {
  const innerProps = {
    ...props.innerProps,
    id: props.selectProps.menuId,
    role: 'listbox',
    // MenuList is only rendered when the dropdown has been expanded
    'aria-expanded': true,
  };
  return <components.MenuList {...props} innerProps={innerProps} />;
};

/**
 * Component has been customized to add complete a11y support for the select component.
 * @see https://github.com/JedWatson/react-select/issues/4062#issuecomment-872356845 for more details on what the current stance of react-select is.
 * @see https://github.com/JedWatson/react-select/pull/4322#issuecomment-749134033 for workaround
 */
const Input = (props: any) => {
  // aria-label and aria-labelledby are already spread by the existing props if provided
  return (
    <components.Input
      // aria-label and aria-labelledby are already spread by the existing props if provided
      {...props}
      role={props.selectProps.isSearchable ? 'combobox' : 'listbox'}
      aria-autocomplete="list"
    />
  );
};

// ====================== shared resources ======================
const OptionContainer = styled.div<{ isSelected: boolean }>`
  width: 100%;
  cursor: pointer;
  &:hover {
    > div {
      background-color: ${colors.brand50};
      color: ${colors.neutrals0};
    }
    #bottomSq {
      background-color: ${(props: any) => (props.isSelected ? colors.brand50 : '')};
    }
  }
`;

export const dropdownIndicatorStyles = {
  margin: '0 0 0 8px',
  color: colors.brand50,
  display: 'flex',
};

export const toggleStyles = (menuIsOpen: boolean): CSSProperties => ({
  alignItems: 'center',
  backgroundColor: menuIsOpen ? '#E5EFF9' : colors.neutrals0,
  borderTopLeftRadius: radii.roundedSquare,
  borderTopRightRadius: radii.roundedSquare,
  borderBottomLeftRadius: menuIsOpen ? 0 : radii.roundedSquare,
  borderBottomRightRadius: menuIsOpen ? 0 : radii.roundedSquare,
  cursor: 'pointer',
  display: 'flex',
  fontWeight: 500,
  height: '45px',
  justifyContent: 'flex-end',
  padding: '0 8px',
});

const optionStyles = {
  alignItems: 'center',
  display: 'flex',
  fontSize: '16px',
  width: '100%',
};
